/* eslint-disable i18next/no-literal-string */
import { useEffect, useRef, type CSSProperties } from "react";
import lottie from "lottie-web";

/**
 * Ported from bouncieweb's `components/LottieAnimation.js`.
 *
 * The original imported the animation JSON directly and passed `animationData`.
 * Under Vite/SSR we instead load the JSON by URL `path` inside a client-only
 * useEffect (per porting rule 8), so no JSON is bundled and nothing touches
 * the DOM during server render. The animation is destroyed on unmount.
 *
 * Callers mount this component conditionally (tied to `useHasBeenOnScreen`) so
 * the effect — and therefore playback — only starts once the item is on screen,
 * exactly as the original did.
 */
export default function LottieAnimation({
  path,
  loop = true,
  styles = undefined,
}: {
  path: string;
  loop?: boolean;
  styles?: CSSProperties;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop,
      autoplay: true,
      path,
    });
    return () => anim.destroy();
  }, [path, loop]);

  return <div ref={containerRef} style={styles} />;
}
